'use client';

import type { UUID } from 'node:crypto';
import { parseDate } from '@internationalized/date';
import { create, type StateCreator } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import type { MapBounds } from '~/components/Playground/mapConstants';
import type { PlaygroundDialogType } from '~/components/Playground/PlaygroundDialog';
import { DEFAULT_ORIGIN_ID } from '~/constants/default-origin';
import type { Countries } from '~/libs/dato/queries/countries';
import type { Place, Places } from '~/libs/dato/queries/places';
import type { ChosenDepartureDate, ChosenDuration } from '~/types/Date.types';
import type { JourneyTransformed } from '~/types/Journey.types';
import type { DpOrigin } from '~/types/Origin.types';
import type {
  PlaygroundContext,
  PlaygroundCountry,
  PlaygroundCountryGroup,
  PlaygroundPlace
} from '~/types/Playground.types';

type DrawerItem = 'start-end' | 'dates' | 'destinations' | 'trip' | 'duration';

export type TripDrawerFocus = 'email' | null;

export type CreateTripTab = 'round-trip' | 'one-way-trip';

type LoadingMessage = {
  text: string;
  duration: number;
};

export interface aiTravelPlannerResponse {
  suggestions?: string;
  output?: string;
  Places?: string[];
  extractedIdeas?: string[];
  original_input?: string;
  PlacesResponse?: {
    Name: string;
    Reasons: string[];
    Suggestions: string; // Change from string[] to string
    Tags: string[];
  }[];
}

export type AiTravelPlannerPlace = Place['place'] & {
  reasons: string[];
  suggestions: string;
  tags: string[];
  name: string;
};

export type TripPanelResult =
  | {
      type: 'update-nights';
      status: 'success' | 'failure';
      id: string;
      place: string;
      nights: number;
      userMessage: string;
    }
  | {
      type: 'reverse';
      status: 'success' | 'failure';
      journeyId: string;
      userMessage: string;
    }
  | null;

interface PlaygroundActions {
  resetState: () => void;
  invalidateTripResults: () => void;
  setContext: (context: PlaygroundContext) => void;
  setOriginId: (originId: number | null) => void; // placeId
  setStartDate: (startDate: string | null) => void; // YYYY-MM-DD
  setEndDate: (endDate: string | null) => void; // YYYY-MM-DD
  setNights: (nights: number | null) => void; // nights
  setChosenDepartureDateType: (chosenDepartureDateType: ChosenDepartureDate | null) => void;
  setSkeletonTrips: (skeletonTrips: JourneyTransformed[]) => void;
  setSkeletonTrip: (newTrip: JourneyTransformed) => void;
  setFullTrip: (skeletonTripId: string, fullTrip: JourneyTransformed | null) => void;
  setAllCmsPlaces: (placesData: Places['allPlaces']) => void;
  setAllCmsPlacesEnglish: (placesData: Places['allPlaces']) => void;
  setAllCmsCountries: (countriesData: Countries['allCountries']) => void;
  setAllDpOrigins: (origins: DpOrigin[]) => void;
  addDestination: (placeId: number, aiSuggestions?: string) => void; // later will need to include countries / country groups
  addUnsupportedDestination: (placeToAdd: PlaygroundPlace) => void;
  removeDestination: (placeId: number) => void; // removes by id (not cms id / 'placeId'), later will need to include countries / country groups
  removeUnsupportedDestination: (placeId: number) => void;
  setCurrentTripIndex: (index: number) => void;
  addSentTripId: (tripId: string) => void;
  // UI setters
  setLastAddedDestination: (placeId: number) => void;
  showDialog: (dialog: PlaygroundDialogType, data?: Record<string, any>) => void;
  hideDialog: () => void;
  showDestinationPanel: (place: Place['place']) => void;
  hideDestinationPanel: () => void;
  openDrawer: (drawerItem: DrawerItem) => void;
  closeDrawer: () => void;
  openTripDrawerWithFocus: (focus: TripDrawerFocus) => void;
  setTripDrawerInitialFocus: (focus: TripDrawerFocus) => void;
  setIsLoading: (isLoading: boolean) => void;
  setLoadingTitle: (loadingTitle: string) => void;
  setShowAnimLoader: (isLoadingTrips: boolean) => void;
  setIsAddingDestination: (isAddingDestination: boolean) => void;
  setRemovingDestination: (destinationId: Set<number>) => void;
  setPlaceDialog: (placeDialog: (Place['place'] & { suggestions?: string }) | null) => void;
  setIsDragging: (isDragging: boolean) => void;
  setShowRoutes: (showRoutes: boolean) => void;
  setDatesTab: (datesTab: 'calendar' | 'nights') => void;
  setCreateTripTab: (createTripTab: CreateTripTab) => void;
  setEmailSuccess: (emailSuccess: boolean) => void;
  setLoadingMessages: (loadingMessages: LoadingMessage[]) => void;
  setAiTravelPlannerResponse: (aiTravelPlannerResponse: aiTravelPlannerResponse | null) => void;
  setCountryExtents: (countryExtents: MapBounds | null) => void;
  setOverviewDisabled: (isOverviewDisabled: boolean) => void;
  setOverviewSubmitting: (isOverviewSubmitting: boolean) => void;
  setTripPanelResult: (tripPanelResult: TripPanelResult | null) => void;
  setSearchValue: (searchValue: string) => void;
}

interface Preferences {
  originId: number | null;
  startDate: string | null;
  endDate: string | null;
  chosenDepartureDateType: ChosenDepartureDate | null;
  chosenDuration: ChosenDuration | null;
  nights: number | null;
  unsupportedDestinations: PlaygroundPlace[];
  lastAddedDestination: number | null;
}

interface PlaygroundState {
  preferences: Preferences;
  context: PlaygroundContext;
  skeletonTrips: JourneyTransformed[];
  fullTrips: Record<string, JourneyTransformed | null>; // keyed by skeleton trip uniqueId
  currentTripIndex: number;
  actions: PlaygroundActions;
  allCmsPlaces: Places['allPlaces'];
  // Always the default (English) locale, regardless of the UI's display locale - used for the
  // AI Travel Planner webhook contract and its response matching, which are locale-independent.
  allCmsPlacesEnglish: Places['allPlaces'];
  allDpOrigins: DpOrigin[];
  allCmsCountries: Countries['allCountries'];
  sentTripIds: string[];
  ui: {
    dialog?: PlaygroundDialogType | null; // null means closed
    dialogData?: Record<string, any> | null; // Add dialogData to store dialog-specific data
    destinationPanel?: Place['place'] | null;
    isDrawerOpen?: boolean;
    drawerItem?: DrawerItem | null;
    isLoading: boolean;
    loadingTitle: string;
    showAnimLoader: boolean;
    isAddingDestination: boolean;
    placeDialog: (Place['place'] & { suggestions?: string }) | null;
    removingDestination: Set<number>;
    isDragging: boolean;
    showRoutes: boolean;
    datesTab: 'calendar' | 'nights';
    createTripTab: CreateTripTab;
    emailSuccess: boolean;
    loadingMessages: LoadingMessage[];
    aiTravelPlannerResponse: aiTravelPlannerResponse | null;
    aiSuggestions: Record<number, string | undefined>; // New field to store AI suggestions
    countryExtents: MapBounds | null;
    isOverviewDisabled: boolean;
    isOverviewSubmitting: boolean;
    tripPanelResult: TripPanelResult | null;
    searchValue: string;
    tripDrawerInitialFocus: TripDrawerFocus;
  };
}

const getPersistedPlaygroundSlice = (state: PlaygroundState) => ({
  preferences: state.preferences,
  context: state.context,
  skeletonTrips: state.skeletonTrips,
  fullTrips: state.fullTrips,
  currentTripIndex: state.currentTripIndex,
  // CMS/DP reference data (places, routes, countries, origins) is deliberately not persisted: it is
  // locale-specific and re-supplied by the server on every page load, so a persisted copy goes stale
  // when the user switches locale within a session.
  sentTripIds: state.sentTripIds,
  ui: {
    ...state.ui,
    // Do not persist non-serializable Set; let it reset to default on load
    removingDestination: undefined
  }
});

const sessionStorageMiddleware: StateStorage = {
  getItem: (name: string) => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return null;
      }
      const value = window.sessionStorage.getItem(name);
      return value ?? null;
    } catch (_error) {
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(name, value);
      }
    } catch (_error) {}
  },
  removeItem: (name: string) => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem(name);
      }
    } catch (_error) {}
  }
};

const LONDON_PLACE = {
  id: 26,
  cm: '93442533',
  n: 'London',
  s: true,
  c: {
    id: 'UK',
    cm: '122318595',
    n: 'United Kingdom',
    s: true,
    p: []
  },
  geo: { latitude: 51.5074, longitude: -0.1278 }
};

export const defaultContext: PlaygroundContext = {
  chosenOrigin: LONDON_PLACE,
  chosenDestination: LONDON_PLACE,
  chosenDepartureDate: null,
  chosenReturnDate: null,
  chosenDuration: null,
  chosenDepartureDateType: null,
  pinnedHighlightedRoutes: null,
  chosenRouteFiltering: null,
  selectedDestinations: {
    countries: [],
    places: []
  },
  availableActivityAttributes: null,
  pinnedActivities: null,
  pinnedPlaces: null,
  chosenPreferences: null,
  highlightedRoutes: null,
  selectableDestinations: [],
  minimumDuration: null,
  recommendedDestinations: null,
  displayActivities: null,
  responseMessages: [],
  playgroundContextId: '',
  // FE ONLY - we use this to check if first run
  isDefaultContext: true
};

export const defaultState = {
  preferences: {
    originId: DEFAULT_ORIGIN_ID,
    startDate: null,
    endDate: null,
    nights: null,
    chosenDepartureDateType: null,
    chosenDuration: null,
    unsupportedDestinations: [] as PlaygroundPlace[],
    lastAddedDestination: null
  },
  allCmsPlaces: [],
  allCmsPlacesEnglish: [],
  allCmsCountries: [],
  allDpOrigins: [],
  alerts: [],
  context: defaultContext,
  skeletonTrips: [],
  fullTrips: {},
  currentTripIndex: 0,
  sentTripIds: [],
  ui: {
    dialog: null,
    dialogData: null,
    destinationPanel: null,
    isDrawerOpen: false,
    drawerItem: null,
    loadingTitle: 'loading',
    isLoading: false,
    showAnimLoader: false,
    isAddingDestination: false,
    placeDialog: null,
    removingDestination: new Set<number>(),
    isDragging: false,
    showRoutes: true,
    datesTab: 'nights' as const,
    createTripTab: 'round-trip' as const,
    emailSuccess: false,
    loadingMessages: [],
    aiTravelPlannerResponse: null,
    aiSuggestions: {}, // Initialize aiSuggestions
    countryExtents: null,
    isOverviewDisabled: false,
    isOverviewSubmitting: false,
    searchValue: '',
    tripPanelResult: null,
    tripDrawerInitialFocus: null as TripDrawerFocus
  }
};

// TODO - separate out different actions / create multiple stores?
const bareStoreSetup: StateCreator<PlaygroundState> = (set, get) => ({
  ...defaultState,
  actions: {
    resetState: () => {
      set({
        ...defaultState,
        ui: {
          ...defaultState.ui,
          aiSuggestions: {}
        }
      });
    },
    // Clears trip-derived outputs that become stale when the origin/destination
    // or trip inputs change. Keeps CMS/DP datasets and current context intact.
    invalidateTripResults: () => {
      const currentState = get();
      set({
        skeletonTrips: [],
        fullTrips: {},
        currentTripIndex: 0,
        sentTripIds: [],
        ui: {
          ...currentState.ui,
          aiSuggestions: {},
          aiTravelPlannerResponse: null,
          tripPanelResult: null,
          placeDialog: null,
          destinationPanel: null,
          removingDestination: new Set<number>()
        }
      });
    },
    setContext: (context: PlaygroundContext) => {
      const currentState = get();
      set({
        ...currentState,
        context,
        ui: {
          ...currentState.ui,
          // Always preserve AI suggestions, response, and search value when context changes
          aiSuggestions: currentState.ui.aiSuggestions,
          aiTravelPlannerResponse: currentState.ui.aiTravelPlannerResponse,
          searchValue: currentState.ui.searchValue,
          // Preserve removingDestination to prevent undefined errors
          removingDestination: currentState.ui.removingDestination
        }
      });
    },
    setOriginId: (originId: number | null) => set({ preferences: { ...get().preferences, originId } }),
    setStartDate: (startDate: string) => {
      const currentState = get();
      set({
        preferences: {
          ...currentState.preferences,
          startDate
        },
        context: {
          ...currentState.context,
          selectedDestinations: {
            ...currentState.context.selectedDestinations,
            places: []
          }
        },
        ui: {
          ...currentState.ui,
          // Clear AI suggestions when destinations are cleared
          aiSuggestions: {}
        }
      });
    },
    setEndDate: (endDate: string) => set({ preferences: { ...get().preferences, endDate } }),
    setNights: (nights: number | null) => set({ preferences: { ...get().preferences, nights } }),
    setChosenDepartureDateType: (chosenDepartureDateType: ChosenDepartureDate | null) => {
      set({
        preferences: { ...get().preferences, chosenDepartureDateType }
      });
    },
    setAllCmsPlaces: (places: Places['allPlaces']) => set({ allCmsPlaces: places }),
    setAllCmsPlacesEnglish: (places: Places['allPlaces']) => set({ allCmsPlacesEnglish: places }),
    setAllDpOrigins: (origins: DpOrigin[]) => set({ allDpOrigins: origins }),
    setAllCmsCountries: (countries: Countries['allCountries']) => set({ allCmsCountries: countries }),
    addDestination: (placeIdToAdd: number, aiSuggestions?: string) => {
      // Store AI suggestions in a new field to persist them
      const currentState = get();
      const newAiSuggestions = {
        ...currentState.ui.aiSuggestions,
        [placeIdToAdd]: aiSuggestions
      };

      set({
        ui: {
          ...currentState.ui,
          aiSuggestions: newAiSuggestions
        }
      });
    },
    addUnsupportedDestination: (placeToAdd: PlaygroundPlace) => {
      const newUnsupportedDestinations = [
        ...get().preferences.unsupportedDestinations.filter((destination) => destination.id !== placeToAdd.id),
        placeToAdd
      ];
      set({
        preferences: {
          ...get().preferences,
          unsupportedDestinations: newUnsupportedDestinations
        }
      });
    },
    removeDestination: (placeIdToRemove: number) => {
      // Remove AI suggestions for this place
      const currentState = get();
      const newAiSuggestions = { ...currentState.ui.aiSuggestions };
      delete newAiSuggestions[placeIdToRemove];

      set({
        ui: {
          ...currentState.ui,
          aiSuggestions: newAiSuggestions
        }
      });
    },
    removeUnsupportedDestination: (destinationId: number) => {
      const newUnsupportedDestinations = get().preferences.unsupportedDestinations.filter(
        (destination) => destination.id !== destinationId
      );
      set({
        preferences: {
          ...get().preferences,
          unsupportedDestinations: newUnsupportedDestinations
        }
      });
    },
    setCurrentTripIndex: (index: number) => {
      set({ currentTripIndex: index });
    },
    setSkeletonTrips: (skeletonTrips: JourneyTransformed[]) => {
      // Clear full trips when skeleton trips change (1:1 mapping)
      set({ skeletonTrips, fullTrips: {} });
    },
    setSkeletonTrip: (newTrip: JourneyTransformed) => {
      const index = get().skeletonTrips.findIndex((trip) => trip.uniqueId === newTrip.uniqueId);

      const newSkeletonTrips = [...get().skeletonTrips];
      if (index === -1) {
        newSkeletonTrips.push(newTrip);
      } else {
        newSkeletonTrips[index] = newTrip;
      }

      const newFullTrips = { ...get().fullTrips };
      delete newFullTrips[newTrip.uniqueId];

      set({ skeletonTrips: newSkeletonTrips, fullTrips: newFullTrips });
    },
    setFullTrip: (skeletonTripId: string, fullTrip: JourneyTransformed | null) => {
      set({
        fullTrips: {
          ...get().fullTrips,
          [skeletonTripId]: fullTrip
        }
      });
    },
    setFullTrips: (fullTrips: Record<UUID, JourneyTransformed | null>) => {
      set({ fullTrips });
    },
    // UI setters
    showDialog: (dialog: PlaygroundDialogType, data?: Record<string, any>) => {
      set({ ui: { ...get().ui, dialog, dialogData: data } });
    },
    hideDialog: () => {
      set({ ui: { ...get().ui, dialog: null, dialogData: null } });
    },
    showDestinationPanel(place: Place['place']) {
      set({ ui: { ...get().ui, destinationPanel: place } });
    },
    hideDestinationPanel() {
      set({ ui: { ...get().ui, destinationPanel: null } });
    },
    openDrawer: (drawerItem: DrawerItem) => {
      set(() => ({ ui: { ...get().ui, isDrawerOpen: true, drawerItem } }));
    },
    closeDrawer: () =>
      set(() => ({
        ui: { ...get().ui, isDrawerOpen: false, drawerItem: null }
      })),
    openTripDrawerWithFocus: (focus: TripDrawerFocus) =>
      set(() => ({
        ui: { ...get().ui, isDrawerOpen: true, drawerItem: 'trip', tripDrawerInitialFocus: focus }
      })),
    setTripDrawerInitialFocus: (focus: TripDrawerFocus) =>
      set(() => ({ ui: { ...get().ui, tripDrawerInitialFocus: focus } })),
    // loadingTitle: 'loading',
    // isLoading: false,
    // isLoadingTrips: false,
    setLoadingTitle: (loadingTitle: string) => {
      set({ ui: { ...get().ui, loadingTitle } });
    },
    setIsLoading: (isLoading: boolean) => {
      set({ ui: { ...get().ui, isLoading } });
    },
    setIsAddingDestination: (isAddingDestination: boolean) => {
      set({ ui: { ...get().ui, isAddingDestination } });
    },
    setShowAnimLoader: (showAnimLoader: boolean) => {
      set({ ui: { ...get().ui, showAnimLoader } });
    },
    setPlaceDialog: (placeDialog: (Place['place'] & { suggestions?: string }) | null) => {
      set({ ui: { ...get().ui, placeDialog } });
    },
    setLastAddedDestination: (placeId: number) => {
      set({
        preferences: {
          ...get().preferences,
          lastAddedDestination: placeId
        }
      });
    },
    setRemovingDestination: (destinationId: Set<number>) => {
      set({ ui: { ...get().ui, removingDestination: destinationId } });
    },
    setIsDragging: (isDragging: boolean) => {
      set({ ui: { ...get().ui, isDragging } });
    },
    setShowRoutes: (showRoutes: boolean) => {
      set({ ui: { ...get().ui, showRoutes } });
    },
    setDatesTab: (datesTab: 'calendar' | 'nights') => {
      set({ ui: { ...get().ui, datesTab } });
    },
    setCreateTripTab: (createTripTab: CreateTripTab) => {
      set({ ui: { ...get().ui, createTripTab } });
    },
    setEmailSuccess: (emailSuccess: boolean) => {
      set({ ui: { ...get().ui, emailSuccess } });
    },
    addSentTripId: (tripId: string) => {
      set({ sentTripIds: [...get().sentTripIds, tripId] });
    },
    setLoadingMessages: (loadingMessages: LoadingMessage[]) => {
      set({ ui: { ...get().ui, loadingMessages } });
    },
    setAiTravelPlannerResponse: (aiTravelPlannerResponse: aiTravelPlannerResponse | null) => {
      set({ ui: { ...get().ui, aiTravelPlannerResponse } });
    },
    setCountryExtents: (countryExtents: MapBounds | null) => {
      set({ ui: { ...get().ui, countryExtents } });
    },
    setOverviewDisabled: (isOverviewDisabled: boolean) => {
      set({ ui: { ...get().ui, isOverviewDisabled } });
    },
    setOverviewSubmitting: (isOverviewSubmitting: boolean) => {
      const tripPanelResult = isOverviewSubmitting ? null : get().ui.tripPanelResult;
      set({ ui: { ...get().ui, isOverviewSubmitting, tripPanelResult } });
    },
    setTripPanelResult: (tripPanelResult: TripPanelResult) => {
      set({ ui: { ...get().ui, tripPanelResult } });
    },
    setSearchValue: (searchValue: string) => {
      set({ ui: { ...get().ui, searchValue } });
    }
  }
});

export const usePlaygroundStore = create<PlaygroundState>()(
  persist(bareStoreSetup, {
    name: 'playground-state',
    storage: createJSONStorage(() => sessionStorageMiddleware),
    partialize: getPersistedPlaygroundSlice,
    onRehydrateStorage: () => (_state, error) => {
      if (error) {
      }
    }
  })
);

// TODO: Memoize selectors where appropriate
export const usePlaygroundPreferences = () => usePlaygroundStore((state) => state.preferences);
export const usePlaygroundActions = () => usePlaygroundStore((state) => state.actions);
export const usePlaygroundUIState = () => usePlaygroundStore((state) => state.ui);
export const usePlaygroundContext = () => usePlaygroundStore((state) => state.context);
// use below not above - pg context is our source of truth, select from it to avoid unnecessary duplication of state
export const usePlaygroundContextParams = () =>
  usePlaygroundStore((state) => {
    return {
      destinations: state.context.selectedDestinations.places,
      startDate: state.context.chosenDepartureDate,
      endDate: state.context.chosenReturnDate,
      originId: state.context.chosenOrigin.id,
      endPlaceId: state?.context?.chosenDestination?.id ?? null,
      chosenOrigin: state.context.chosenOrigin,
      chosenDestination: state.context.chosenDestination,
      chosenDepartureDateType: state.context.chosenDepartureDateType,
      chosenDuration: state.context.chosenDuration,
      minimumDuration: state.context.minimumDuration
    };
  });

// select the duration using start and end dates
export const usePlaygroundNights = () =>
  usePlaygroundStore((state) => {
    if (!state.context.chosenDepartureDate || !state.context.chosenReturnDate) return null;

    const startDate = parseDate(state.context.chosenDepartureDate);
    const endDate = parseDate(state.context.chosenReturnDate);
    const nights = endDate.compare(startDate);

    return nights;
  });

export const usePlaygroundDateType = () =>
  // NOTE: This is not the same thing/concept as what comes back on context. This is whether we have used nights or dates.
  usePlaygroundStore((state) => {
    // if nights have been selected
    if (state.preferences.nights) {
      return 'nights';
    }
    // if start and end date have been selected
    if (state.preferences.startDate && state.preferences.endDate) {
      return 'calendar';
    }
    return '';
  });

export const usePlaygroundAllCmsPlaces = () => usePlaygroundStore((state) => state.allCmsPlaces);

// placeId -> always-English placeName, for the AI Travel Planner webhook contract and response
// matching, which must stay locale-independent regardless of the UI's display locale.
export const usePlaygroundEnglishPlaceNameByPlaceId = () =>
  usePlaygroundStore((state) => {
    const map = new Map<number, string>();
    state.allCmsPlacesEnglish.forEach((place) => {
      if (place.placeId != null) map.set(place.placeId, place.placeName);
    });
    return map;
  });

export const getEnglishPlaceName = (
  englishPlaceNameByPlaceId: Map<number, string>,
  place: { placeId: number | null; placeName: string }
): string =>
  place.placeId != null ? (englishPlaceNameByPlaceId.get(place.placeId) ?? place.placeName) : place.placeName;

export const usePlaygroundSkeletonTrips = () => usePlaygroundStore((state) => state.skeletonTrips);
export const usePlaygroundFullTrips = () => usePlaygroundStore((state) => state.fullTrips);
export const usePlaygroundCurrentTripIndex = () => usePlaygroundStore((state) => state.currentTripIndex);
export const usePlaygroundCurrentSkeletonTrip = () =>
  usePlaygroundStore((state) => state.skeletonTrips[state.currentTripIndex]);
export const usePlaygroundCurrentFullTrip = () =>
  usePlaygroundStore((state) => {
    const currentTrip = state.skeletonTrips[state.currentTripIndex];
    if (!currentTrip) return null;
    return state.fullTrips[currentTrip.uniqueId] ?? null;
  });

export const usePlaygroundAllCmsPlaceNames = () =>
  usePlaygroundStore((state) => {
    const allCmsPlaces = state.allCmsPlaces.map((place) => place.placeName);
    return allCmsPlaces;
  });

export const usePlaygroundOrigin = () =>
  usePlaygroundStore((state) => {
    const context = state.context;
    if (!context)
      return {
        dpOrigin: null,
        cmsOrigin: null
      };
    return {
      dpOrigin: state.context.chosenOrigin,
      cmsOrigin: state.allCmsPlaces.find((place) => place.placeId === state.context.chosenOrigin.id)
    };
  });

export const usePlaygroundEndPlace = () =>
  usePlaygroundStore((state) => {
    const context = state.context;
    if (!context)
      return {
        dpEndPlace: null,
        cmsEndPlace: null
      };

    const chosenDestination = state.context.chosenDestination;

    return {
      dpEndPlace: chosenDestination,
      cmsEndPlace: chosenDestination ? state.allCmsPlaces.find((place) => place.placeId === chosenDestination.id) : null
    };
  });

export const usePlaygroundLastAddedDestination = () =>
  usePlaygroundStore((state) => state.preferences.lastAddedDestination);

export const usePlaygroundSelectedDestinations = () =>
  usePlaygroundStore((state) => {
    const context = state.context;
    const allCmsPlaces = state.allCmsPlaces;
    const filteredPlaces = allCmsPlaces.filter((cmsPlace) =>
      context.selectedDestinations.places.some((place) => place.id === cmsPlace.placeId)
    );
    const ids = context.selectedDestinations.places.map((place) => place.id);

    // DP places/countries carry engine-native (always-English) names; swap in the locale-resolved
    // CMS name by id, same as usePlaygroundDestinations, so selected-destination chips match the UI locale.
    const placeNameByPlaceId = new Map(
      allCmsPlaces.filter((place) => place.placeId != null).map((place) => [place.placeId as number, place.placeName])
    );
    const countryNameByCountryId = new Map(
      state.allCmsCountries.map((country) => [country.countryId, country.countryName])
    );
    const localizedDpPlaces = context.selectedDestinations.places.map((place) => ({
      ...place,
      n: placeNameByPlaceId.get(place.id) ?? place.n,
      c: { ...place.c, n: countryNameByCountryId.get(place.c.id) ?? place.c.n }
    }));

    return {
      cmsPlaces: filteredPlaces,
      dpPlaces: localizedDpPlaces,
      dpIds: ids
    };
  });

export const usePlaygroundSentTripIds = () => usePlaygroundStore((state) => state.sentTripIds);

export const usePlaygroundIsTripSent = (tripId: string) =>
  usePlaygroundStore((state) => state.sentTripIds.includes(tripId));

export const usePlaygroundIsDragging = () => usePlaygroundStore((state) => state.ui.isDragging);

export const usePlaygroundDatesTab = () => usePlaygroundStore((state) => state.ui.datesTab);

export const usePlaygroundCreateTripTab = () => usePlaygroundStore((state) => state.ui.createTripTab);

export const usePlaygroundaiTravelPlannerResponse = () =>
  usePlaygroundStore((state) => state.ui.aiTravelPlannerResponse);

export const usePlaygroundSearchValue = () => usePlaygroundStore((state) => state.ui.searchValue);

// New getter for AI suggestions
export const usePlaygroundAiSuggestions = () => usePlaygroundStore((state) => state.ui.aiSuggestions);

// Get AI suggestions for a specific place
export const usePlaygroundPlaceAiSuggestions = (placeId?: number | null) =>
  usePlaygroundStore((state) => (placeId != null ? state.ui.aiSuggestions[placeId] : undefined));

//// OTHER SELECTORS i.e. derived from state - kept here to avoid exposing full store via export
export const usePlaygroundDestinations = () =>
  // TODO useCallback
  usePlaygroundStore((state) => {
    const { dpIds, dpDestinations } = getSelectableDestinationsFromContext(state.context);

    // DP places/countries carry engine-native (always-English) names; swap in the
    // locale-resolved CMS name by id so the destination picker matches the UI locale.
    const placeNameByPlaceId = new Map(
      state.allCmsPlaces
        .filter((place) => place.placeId != null)
        .map((place) => [place.placeId as number, place.placeName])
    );
    const countryNameByCountryId = new Map(
      state.allCmsCountries.map((country) => [country.countryId, country.countryName])
    );
    const localizeDpPlace = (place: PlaygroundPlace): PlaygroundPlace => ({
      ...place,
      n: placeNameByPlaceId.get(place.id) ?? place.n,
      c: { ...place.c, n: countryNameByCountryId.get(place.c.id) ?? place.c.n }
    });
    dpDestinations.selectable.places = dpDestinations.selectable.places.map(localizeDpPlace);
    dpDestinations.unselectable.places = dpDestinations.unselectable.places.map(localizeDpPlace);
    dpDestinations.all.places = dpDestinations.all.places.map(localizeDpPlace);

    const selectablePlaces = state.allCmsPlaces
      .filter((cmsPlace) => {
        return (
          cmsPlace.placeId !== null &&
          dpIds.selectable.placeIds.includes(cmsPlace.placeId) &&
          cmsPlace.placeId !== state.context.chosenOrigin.id &&
          cmsPlace.placeId !== state.context.chosenDestination?.id
        );
      })
      .sort((a, b) => a.placeName.localeCompare(b.placeName));

    const unselectablePlaces = state.allCmsPlaces
      .filter((cmsPlace) => {
        return (
          cmsPlace.placeId !== null &&
          dpIds.unselectable.placeIds.includes(cmsPlace.placeId) &&
          cmsPlace.placeId !== state.context.chosenOrigin.id &&
          cmsPlace.placeId !== state.context.chosenDestination?.id
        );
      })
      .sort((a, b) => a.placeName.localeCompare(b.placeName));

    const selectableCountries = state.allCmsCountries.filter((cmsCountry) =>
      dpIds.selectable.countryIds.includes(cmsCountry.countryId)
    );
    const unselectableCountries = state.allCmsCountries.filter((cmsCountry) =>
      dpIds.unselectable.countryIds.includes(cmsCountry.countryId)
    );
    // NOTE: In future - add country groups here

    const unsupportedCmsPlaces = state.allCmsPlaces.filter((cmsPlace) => {
      if (cmsPlace.placeId == null) {
        return false;
      }
      const isSelectable = dpIds.selectable.placeIds.includes(cmsPlace.placeId);
      const isUnselectable = dpIds.unselectable.placeIds.includes(cmsPlace.placeId);
      return !isSelectable && !isUnselectable;
    });

    // e.g. places that actually exist both in JAI Maps and CMS, so we can display them
    const availableCmsPlaces = [...selectablePlaces, ...unselectablePlaces];

    const cmsDestinations = {
      selectable: {
        cmsPlaces: selectablePlaces ?? [],
        cmsCountries: selectableCountries ?? [],
        cmsCountryGroups: [] // placeholder - country groups not implemented
      },
      unselectable: {
        cmsPlaces: unselectablePlaces ?? [],
        cmsCountries: unselectableCountries ?? [],
        cmsCountryGroups: [] // placeholder
      },
      unsupported: {
        cmsPlaces: unsupportedCmsPlaces ?? [],
        cmsCountries: [], // TODO: Add unsupported countries if needed
        cmsCountryGroups: [] // placeholder
      },
      all: {
        cmsPlaces: state.allCmsPlaces,
        cmsCountries: state.allCmsCountries
        // TODO cmsCountryGroups
      },
      available: {
        cmsPlaces: availableCmsPlaces
      }
    };

    const validUnsupportedCmsPlaces = unsupportedCmsPlaces.filter((cmsPlace) => cmsPlace.placeId !== null);

    // transformation for CMSPlace[]
    const unsupportedDpPlaces = validUnsupportedCmsPlaces.map((cmsPlace) => ({
      id: cmsPlace.placeId!,
      cm: cmsPlace.id,
      n: cmsPlace.placeName,
      s: true,
      geo: {
        latitude: cmsPlace.location.latitude,
        longitude: cmsPlace.location.longitude
      },
      c: {
        cm: cmsPlace.country.id,
        id: cmsPlace.country.countryId,
        n: cmsPlace.country.countryName || '',
        s: true,
        p: []
      }
    }));
    dpDestinations.unsupported.places = unsupportedDpPlaces;
    dpIds.unsupported.placeIds = unsupportedDpPlaces.map((place) => place.id);

    const destinations = {
      dpDestinations,
      dpIds,
      cmsDestinations
    };

    return destinations;
  });

export function getSelectableDestinationsFromContext(playgroundContext: PlaygroundContext) {
  const selectablePlaceIdsSet: Set<number> = new Set();
  const unselectablePlaceIdsSet: Set<number> = new Set();
  const selectableCountryIdsSet: Set<string> = new Set();
  const unselectableCountryIdsSet: Set<string> = new Set();
  const selectableCountryGroupIdSet: Set<string> = new Set();
  const unselectableCountryGroupIdSet: Set<string> = new Set();
  const selectablePlaceSet: Set<PlaygroundPlace> = new Set();
  const selectableCountrySet: Set<PlaygroundCountry> = new Set();
  const selectableCountryGroupSet: Set<PlaygroundCountryGroup> = new Set();
  const unselectablePlaceSet: Set<PlaygroundPlace> = new Set();
  const unselectableCountrySet: Set<PlaygroundCountry> = new Set();
  const unselectableCountryGroupSet: Set<PlaygroundCountryGroup> = new Set();

  playgroundContext.selectableDestinations.forEach((countryGroup) => {
    if (countryGroup.s) {
      selectableCountryGroupIdSet.add(countryGroup.id);
      selectableCountryGroupSet.add(countryGroup);
    } else {
      unselectableCountryGroupIdSet.add(countryGroup.id);
      unselectableCountryGroupSet.add(countryGroup);
    }
    countryGroup.c.forEach((country) => {
      if (country.s) {
        selectableCountryIdsSet.add(country.id);
        selectableCountrySet.add(country);
      } else {
        unselectableCountryGroupIdSet.add(country.id);
        unselectableCountrySet.add(country);
      }
      country.p.forEach((place) => {
        const placeWithCountry = {
          ...place,
          country: {
            ...country
          }
        };
        if (place.s) {
          selectablePlaceIdsSet.add(place.id);
          selectablePlaceSet.add(placeWithCountry);
        } else {
          unselectablePlaceIdsSet.add(place.id);
          unselectablePlaceSet.add(placeWithCountry);
        }
      });
    });
  });

  return {
    dpDestinations: {
      selectable: {
        places: Array.from(selectablePlaceSet),
        countries: Array.from(selectableCountrySet),
        countryGroups: Array.from(selectableCountryGroupSet)
      },
      unselectable: {
        places: Array.from(unselectablePlaceSet),
        countries: Array.from(unselectableCountrySet),
        countryGroups: Array.from(unselectableCountryGroupSet)
      },
      unsupported: {
        places: [] as PlaygroundPlace[],
        countries: [],
        countryGroups: []
      },
      all: {
        places: Array.from(selectablePlaceSet).concat(Array.from(unselectablePlaceSet)),
        countries: Array.from(selectableCountrySet).concat(Array.from(unselectableCountrySet)),
        countryGroups: Array.from(selectableCountryGroupSet).concat(Array.from(unselectableCountryGroupSet))
      }
    },
    dpIds: {
      selectable: {
        placeIds: Array.from(selectablePlaceIdsSet),
        countryIds: Array.from(selectableCountryIdsSet),
        countryGroupIds: Array.from(selectableCountryGroupIdSet)
      },
      unselectable: {
        placeIds: Array.from(unselectablePlaceIdsSet),
        countryIds: Array.from(unselectableCountryIdsSet),
        countryGroupIds: Array.from(unselectableCountryGroupIdSet)
      },
      unsupported: {
        placeIds: [] as number[],
        countryIds: [],
        countryGroupIds: []
      },
      all: {
        placeIds: Array.from(selectablePlaceIdsSet).concat(Array.from(unselectablePlaceIdsSet)),
        countryIds: Array.from(selectableCountryIdsSet).concat(Array.from(unselectableCountryIdsSet)),
        countryGroupIds: Array.from(selectableCountryGroupIdSet).concat(Array.from(unselectableCountryGroupIdSet))
      }
    }
  };
}
